import axios from "axios";

/**
 * MASTER_PLAN.md §3.9 work items #12/#15 — thin API wrapper around the
 * maintenance-module backend surface (service-plan, service-plan-lcp,
 * work-order, contact modules — see js_crm_backend Session 34/35).
 * Mirrors the existing api/* conventions exactly (query passthrough,
 * try/catch returning e.response.data on failure).
 *
 * Session 39: added Work Order matching (§3.9 #16) and Convert (§3.9 #6)
 * wrappers for the Review Queue page.
 *
 * Session 45: added the Batch roster surface (allocate Locations to a
 * Batch, read a Batch's roster, per-Batch stats) — see
 * docs/maintenance-module/design/BATCHES.md.
 */

// ─── Service Plans ────────────────────────────────────────────────

export const getServicePlans = async ({ query = {}, controller = null, excludeInterceptor = false }: any) => {
	const params: any = { params: query, excludeInterceptor };
	if (controller?.signal) {
		params.signal = controller.signal;
	}

	try {
		const res: any = await axios.get("/maintenance/plans", params);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getServicePlan = async ({ data, controller = null }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/plans/${data?.id}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// KNOWN_ISSUES item 18 — the client's one write path. Raises a work
// request against a plan the client is opted into. The SERVER decides
// where it lands (on the plan vs quoted separately) from the location,
// and returns `landedOnPlan` so the confirmation copy cannot promise
// something different from what happened. `requestToken` is a
// client-generated idempotency key — send the SAME one on a retry, a
// NEW one on a new request.
export const createClientWorkRequest = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/plans/${data?.planId}/client-requests`, {
			locationId: data?.locationId,
			problemDescription: data?.problemDescription,
			...(data?.urgency ? { urgency: data.urgency } : {}),
			...(data?.joinPlanRequested ? { joinPlanRequested: true } : {}),
			...(data?.requestToken ? { requestToken: data.requestToken } : {}),
		});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const createServicePlan = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/plans`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// OPERATING_MODEL.md — quote-fed direct quote. Produces a plan-tagged
// RFQ seeded from a location on this (quote-fed) plan; returns { rfqId }.
// The caller navigates to /quote-requests/:rfqId to continue in the
// trunk RFQ UI. See design/DIRECT_QUOTE_QUOTE_FED.md.
export const createPlanDirectQuote = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/plans/${data?.planId}/direct-quote`, {
			lcpId: data?.lcpId,
			serviceDate: data?.serviceDate,
			instructions: data?.instructions,
			...(data?.productId ? { productId: data.productId } : {}),
			...(data?.quantity != null ? { quantity: data.quantity } : {}),
		});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const updateServicePlan = async ({ data, update }: any) => {
	try {
		const res: any = await axios.patch(`/maintenance/plans/${data?.id}`, update);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const closeServicePlan = async ({ data }: any) => {
	try {
		const res: any = await axios.patch(`/maintenance/plans/${data?.id}/close`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// ─── Plan Sites (service_plan_lcp join) ───────────────────────────

// P-a (mockup 26) — the PAGED roster read for Plan Detail at scale.
// Returns { items, total, page, pageSize, counts }, with every filter,
// the sort and the paging applied server-side. `counts` carries the
// saved-view chip totals against the whole plan, not the active filter.
// The unpaged getPlanEnrichedSites stays for callers that genuinely
// want the entire roster (import / reconciliation paths).
export const getPlanEnrichedSitesPage = async ({ data }: any) => {
	const empty = { items: [], total: 0, page: 1, pageSize: 50, counts: {} };
	try {
		const params: any = {
			page: data?.page ?? 1,
			pageSize: data?.pageSize ?? 50,
		};
		if (data?.search) params.search = data.search;
		if (data?.status) params.status = data.status;
		if (data?.cadence) params.cadence = data.cadence;
		// TRI-STATE. A truthy check would drop `false`, which is the
		// "nothing raised against it" view — the actionable one.
		if (data?.hasOpenWo === true) params.hasOpenWo = "true";
		else if (data?.hasOpenWo === false) params.hasOpenWo = "false";
		if (data?.groupId) params.groupId = data.groupId;
		if (data?.unbatched) params.unbatched = "true";
		if (data?.region) params.region = data.region;
		if (data?.sort) params.sort = data.sort;
		if (data?.sortDir) params.sortDir = data.sortDir;

		// excludeInterceptor: the global axios response interceptor turns
		// ANY GET 404 into the app-wide 404 page. This endpoint is newer
		// than the deployed API on some environments, so without this a
		// missing route takes the whole Plan Detail page down instead of
		// degrading one table.
		const res: any = await axios.get(
			"/maintenance/plans/" + data?.id + "/sites/page",
			{ params, excludeInterceptor: true } as any,
		);
		return res?.data ? res.data : empty;
	} catch (e: any) {
		// Distinguish "the API does not have this route yet" from "this
		// plan genuinely has no locations" so the table can say which.
		return { ...empty, error: true, status: e?.response?.status ?? null };
	}
};

export const getPlanSites = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/plans/${data?.planId}/sites`);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getServicePlanSiteCounts = async ({ data }: any) => {
	try {
		const ids = Array.isArray(data?.planIds) ? data.planIds.join(",") : "";
		if (!ids) return {};
		const res: any = await axios.get(`/maintenance/plans/site-counts`, { params: { ids } });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return {};
	}
};

// v3.4 UX pass — real per-plan last-activity timestamp for the
// client-detail Plans tab (replaces the misleading `plan.updatedAt`
// proxy). Returns Record<planId, ISO string>; plans with no events
// are absent from the map. Backend endpoint is client-viewer safe:
// filters planIds by ownership before computing.
export const getServicePlanLastActivity = async ({ data }: any) => {
	try {
		const ids = Array.isArray(data?.planIds) ? data.planIds.join(",") : "";
		if (!ids) return {};
		const res: any = await axios.get(`/maintenance/plans/last-activity`, { params: { ids } });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return {};
	}
};

// Session 46 DRIFT-5 — bulk per-plan progress for the Maintenance index
// Progress column. Returns Record<planId, { sitesInPlan, sitesServiced,
// committed, openWos }>; plans absent from a stream default to zeros.
// Client-viewer safe (ownership-filtered server-side).
export const getServicePlanProgress = async ({ data }: any) => {
	try {
		const ids = Array.isArray(data?.planIds) ? data.planIds.join(",") : "";
		if (!ids) return {};
		const res: any = await axios.get(`/maintenance/plans/progress`, { params: { ids } });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return {};
	}
};

// §3.9 #22 — plan detail aggregates (mockup 13). Stats power the 4-card
// row; enriched sites feed the enriched table with pills; activity
// backs the right-rail recent feed.

export const getPlanStats = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/plans/${data?.id}/stats`);
		return res?.data
			? res.data
			: { sitesInPlan: 0, sitesServiced: 0, openWos: 0, yardsYtd: 0 };
	} catch (e: any) {
		return { sitesInPlan: 0, sitesServiced: 0, openWos: 0, yardsYtd: 0 };
	}
};

// Session 45 — optional `groupId` scopes the list to one Batch's
// roster, server-side. Omit it for the full plan list.
export const getPlanEnrichedSites = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/plans/${data?.id}/sites/enriched`, {
			params: data?.groupId ? { groupId: data.groupId } : undefined,
		});
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

// N5 — plan locations map. Lean, non-paginated: every plan location with
// coordinates + a map-status bucket, fetched once when the map panel opens.
// Optional groupId scopes to one batch.
export const getPlanLocationMap = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/plans/${data?.id}/locations/map`, {
			params: data?.groupId ? { groupId: data.groupId } : undefined,
		});
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getPlanActivity = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/plans/${data?.id}/activity`, {
			params: data?.limit ? { limit: data.limit } : undefined,
		});
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const addPlanSite = async ({ data }: any) => {
	try {
		// v3.4 UX pass — accept EITHER lcpId (legacy) or locationId (the
		// single-Location picker on Plan Detail). Backend resolves LCP
		// via findOrCreate when locationId is passed — staff doesn't have
		// to know LCPs exist.
		const payload: any = {};
		if (data?.lcpId) payload.lcpId = data.lcpId;
		if (data?.locationId) payload.locationId = data.locationId;
		const res: any = await axios.post(`/maintenance/plans/${data?.planId}/sites`, payload);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const removePlanSite = async ({ data }: any) => {
	try {
		const res: any = await axios.delete(`/maintenance/plans/${data?.planId}/sites/${data?.lcpId}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// ─── Plan Groups (work_order_group) — not surfaced in the Plans tab
// yet (no groups UI in this pass); wrapper included so plan detail
// work (§3.9 #22) doesn't need a second API file.

export const getPlanGroups = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/plans/${data?.planId}/groups`);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const createPlanGroup = async ({ data, create }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/plans/${data?.planId}/groups`, create);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const updatePlanGroup = async ({ data, update }: any) => {
	try {
		const res: any = await axios.patch(
			`/maintenance/plans/${data?.planId}/groups/${data?.groupId}`,
			update,
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// ─── Batch roster (work_order_group_lcp, Session 45) ───────────────
//
// The roster is the list of Locations committed to a Batch. Sites land
// here BEFORE any Work Order exists — EMCOR commits 125 Locations to
// FY27 Batch 1 up front and releases them for work over the following
// months. See docs/maintenance-module/design/BATCHES.md.

export const getGroupRoster = async ({ data }: any) => {
	try {
		const res: any = await axios.get(
			`/maintenance/plans/${data?.planId}/groups/${data?.groupId}/roster`,
		);
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

// Bulk by design — a 125-Location commitment is one call, not 125.
export const addToGroupRoster = async ({ data }: any) => {
	try {
		const res: any = await axios.post(
			`/maintenance/plans/${data?.planId}/groups/${data?.groupId}/roster`,
			{ lcpIds: data?.lcpIds || [] },
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// 409s when the Site already has a WO against it in this Batch —
// surface the message rather than swallowing it.
export const removeFromGroupRoster = async ({ data }: any) => {
	try {
		const res: any = await axios.delete(
			`/maintenance/plans/${data?.planId}/groups/${data?.groupId}/roster/${data?.lcpId}`,
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Per-Batch progress. Replaces dividing plan-level `stats.sitesServiced`
// by a batch's committedCount, which double-counted across batches.
export const getGroupStats = async ({ data }: any) => {
	try {
		const res: any = await axios.get(
			`/maintenance/plans/${data?.planId}/groups/${data?.groupId}/stats`,
		);
		return res?.data
			? res.data
			: { committedCount: 0, rosterSize: 0, planned: 0, woCreated: 0, serviced: 0, progressPct: 0 };
	} catch (e: any) {
		return { committedCount: 0, rosterSize: 0, planned: 0, woCreated: 0, serviced: 0, progressPct: 0 };
	}
};

// ─── Site history (service_event timeline) ────────────────────────

export const getSiteHistory = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/sites/${data?.lcpId}/history`);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

// §3.9 #20 — which plan(s) cover a given Site (LCP). Feeds the Location
// Detail Plan Activity panel's render gate: empty array means the panel
// doesn't render at all for this Location/viewer.
export const getSitePlans = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/sites/${data?.lcpId}/plans`);
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

// ─── Instant-Estimate product source (Session 48) ─────────────────
//
// The one shared definition of "what a maintenance quote can be for" —
// the same non-addon, template-backed, depot-covered products a normal
// RFQ reaches, resolved through the same engine. Returns
// [{ id, name, unit, coverageCount, templateCount }] ordered by real
// coverage (index 0 is the primary product). See
// docs/maintenance-module/design/DIRECT_QUOTE_QUOTE_FED.md §6.1.
export const getInstantEstimateProducts = async ({ controller = null }: any = {}) => {
	const params: any = {};
	if (controller?.signal) params.signal = controller.signal;
	try {
		const res: any = await axios.get("/maintenance/instant-estimate-products", params);
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

// ─── Work Orders ───────────────────────────────────────────────────

export const getWorkOrders = async ({ query = {}, controller = null, excludeInterceptor = false }: any) => {
	const params: any = { params: query, excludeInterceptor };
	if (controller?.signal) {
		params.signal = controller.signal;
	}

	try {
		const res: any = await axios.get("/maintenance/work-orders", params);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

// Scale pass 2 (a) — the WO Review queue's four stat cards as a cheap
// server-side aggregate. Replaces the old pattern of fetching every WO
// for the company (getWorkOrders({ query: {} })) just to count them.
// Returns the four numbers directly; falls back to zeros on error so the
// header renders rather than throwing.
/**
 * Session 76 — manual trigger for the work order email intake.
 *
 * Non-production environments never subscribe a Graph webhook
 * (`onModuleInit` skips SANDBOX/BETA/STAGING), so nothing calls
 * processInbox on its own there. Production only self-triggers once
 * WORKORDER_INTAKE_LIVE is "true". This wrapper is what the queue's
 * "Check for new work orders" button calls so testing does not require
 * a devtools console, and so staff have a "check now" after go-live.
 *
 * Returns the raw { processed, skipped, errors, details } envelope.
 */
/**
 * Client-initiated pricing for a work order that already exists but has
 * no RFQ yet — the "Needs RFQ" rows. The client supplies only the
 * quantity; the plan supplies the product server-side.
 */
export const priceClientWorkOrder = async ({ data }: any) => {
	try {
		const res: any = await axios.post(
			`/maintenance/plans/${data?.planId}/client-requests/${data?.workOrderId}/price`,
			{ quantity: data?.quantity },
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/**
 * Session 76 — manual trigger for the work order email intake.
 *
 * Non-production environments never subscribe a Graph webhook
 * (`onModuleInit` skips SANDBOX/BETA/STAGING), so nothing calls
 * processInbox on its own there. Production only self-triggers once
 * WORKORDER_INTAKE_LIVE is "true". This wrapper is what the queue's
 * "Check for new work orders" button calls so testing does not require
 * a devtools console, and so staff have a "check now" after go-live.
 *
 * Returns the raw { processed, skipped, errors, details } envelope.
 */
export const processWorkOrderIntake = async ({ controller = null }: any = {}) => {
	const params: any = {};
	if (controller?.signal) {
		params.signal = controller.signal;
	}
	try {
		const res: any = await axios.post("/work-order-intake/process", {}, params);
		return res?.data?.data ? res.data.data : res?.data;
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getWorkOrderCounts = async ({ controller = null }: any = {}) => {
	const params: any = {};
	if (controller?.signal) {
		params.signal = controller.signal;
	}
	try {
		const res: any = await axios.get("/maintenance/work-orders/counts", params);
		return res?.data
			? res.data
			: { receivedToday: 0, unmatched: 0, ready: 0, rfqGeneratedThisWeek: 0 };
	} catch (e: any) {
		return { receivedToday: 0, unmatched: 0, ready: 0, rfqGeneratedThisWeek: 0 };
	}
};

export const createWorkOrder = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Session 65 - duplicate one or more work orders (clone-for-revisit).
// Takes { ids: string[] }; returns { success, created, count }.
export const duplicateWorkOrders = async ({ data }: { data: { ids: string[] } }) => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders/duplicate`, { ids: data?.ids ?? [] });
		return res?.data ? res.data : { success: false };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { success: false };
	}
};

export const getWorkOrder = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/work-orders/${data?.id}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Session 48 — WO detail page save. Patches intake-carried fields only
// (problem, priority, NTE, SLA dates, externalId, receivedAt). `update`
// is the partial body; only supplied keys are patched, null clears a
// nullable field. Site/plan/batch/status move through their own
// wrappers (manualMatch / assignWorkOrderGroup / convert / reject).
export const updateWorkOrder = async ({ data, update }: any) => {
	try {
		const res: any = await axios.patch(`/maintenance/work-orders/${data?.id}`, update ?? {});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const rejectWorkOrder = async ({ data }: any) => {
	try {
		const res: any = await axios.patch(`/maintenance/work-orders/${data?.id}/reject`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// ─── Work Order Matching (§3.9 #16, Session 39) ────────────────────

export const getMatchCandidates = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/work-orders/${data?.id}/match-candidates`);
		return res?.data ? res.data : { status: "error", reason: "request_failed", candidates: [] };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { status: "error", reason: "request_failed", candidates: [] };
	}
};

export const manualMatchWorkOrder = async ({ data }: any) => {
	try {
		const res: any = await axios.patch(`/maintenance/work-orders/${data?.id}/match`, { lcpId: data?.lcpId });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const rematchWorkOrder = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders/${data?.id}/rematch`);
		return res?.data ? res.data : { status: "error", reason: "request_failed", candidates: [] };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { status: "error", reason: "request_failed", candidates: [] };
	}
};

// v3.4 Session 44 — assign / unassign a WO to a Batch (work_order_group).
// Backend endpoint has existed since Session 34; nothing in the frontend
// called it until now, which is why plans-with-batches always showed
// zero WOs against every batch even after staff configured them (see
// docs/maintenance-module/design/BATCHES.md).
export const assignWorkOrderGroup = async ({ data }: any) => {
	try {
		const res: any = await axios.patch(`/maintenance/work-orders/${data?.id}/group`, {
			groupId: data?.groupId ?? null,
		});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// KNOWN_ISSUES item 20 (Session 73) — move a WO between maintenance
// plans, or off plans with servicePlanId: null. Site and batch already
// had re-assignment routes; plan did not, so a WO under the wrong
// parent could not be moved at all.
//
// The response carries `previousPlanStillEnrolled`: the move does NOT
// un-enrol the site from the old plan (that is a product decision, and
// the site may carry other work there), so the caller is told what was
// left behind rather than having it removed silently.
export const assignWorkOrderPlan = async ({ data }: any) => {
	try {
		const res: any = await axios.patch(`/maintenance/work-orders/${data?.id}/plan`, {
			servicePlanId: data?.servicePlanId ?? null,
		});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// ─── Work Order Convert (§3.9 #6, Session 38) ──────────────────────

export const getConvertPreview = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/work-orders/${data?.id}/convert-preview`);
		return res?.data ? res.data : { ready: false, blockers: ["Unable to load preview."], extracted: null, proposed: null };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { ready: false, blockers: ["Unable to load preview."], extracted: null, proposed: null };
	}
};

export const convertWorkOrder = async ({ data, overrides }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders/${data?.id}/convert`, overrides ?? {});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Session 56 (Part 1 follow-on) — reconcile the attached RFQ to the work
// order on demand (service selection + quantity). The WO detail page
// calls this when it detects the priced line has drifted from the work
// order (wrong quantity, or an unselected service the WO already names).
// No-op server-side once a quote exists. Returns the updated WorkOrder.
export const reconcileWorkOrderRfq = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders/${data?.id}/reconcile-rfq`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Operator's "re-price RFQ to match the work order" action. Re-prices the
// RFQ to the WO's service + quantity even when a not-yet-accepted quote
// exists (superseding it and resetting the RFQ so a corrected quote can
// be generated). Server 400s when the quote is accepted or has a
// project/invoice. Returns the updated WorkOrder or an error payload.
export const forceRepriceWorkOrderRfq = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders/${data?.id}/reprice-rfq`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Cancel a work order (soft) — keeps the record + history and any RFQ /
// quote / project chain, but stops it driving active views. The right
// action for a WO already in flight. Cascades the teardown down the chain
// and returns a summary: { workOrder, cancelled, alreadyCancelled,
// projectsCancelled, quotesSuperseded, posSoftDeleted, invoicesArchived,
// needsProviderNotice[], needsFinanceReview[] }.
export const cancelWorkOrder = async ({ data }: any) => {
	try {
		const res: any = await axios.patch(
			`/maintenance/work-orders/${data?.id}/cancel`,
			{ reason: data?.reason ?? null },
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// CLIENT CANCELLATION REQUEST (Session 80). The client ASKS; they never
// cancel — cancelling auto-cancels sent POs and refuses to touch paid
// invoices, so it is a human call by construction. All three return the
// updated work order (field-stripped for a client), or an error payload
// carrying a `message`.
export const requestWorkOrderCancellation = async ({ data }: any) => {
	try {
		const res: any = await axios.post(
			`/maintenance/work-orders/${data?.id}/cancellation-request`,
			{
				reason: data?.reason ?? null,
				reasonCategory: data?.reasonCategory ?? null,
			},
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// The client changes their mind. Without this, changing their mind means
// phoning us — the same friction the request exists to remove.
export const withdrawWorkOrderCancellation = async ({ data }: any) => {
	try {
		const res: any = await axios.delete(
			`/maintenance/work-orders/${data?.id}/cancellation-request`,
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Staff decline the request; the work continues. NOT a cancel — staff
// APPROVING is `cancelWorkOrder` above, which clears the request as part
// of the cascade.
export const dismissWorkOrderCancellation = async ({ data }: any) => {
	try {
		const res: any = await axios.post(
			`/maintenance/work-orders/${data?.id}/cancellation-request/dismiss`,
			{ note: data?.note ?? null },
		);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// Hard delete a work order — only permitted before an RFQ exists
// (the server 400s otherwise). Returns { success: true } or an error
// payload with a message.
export const deleteWorkOrder = async ({ data }: any) => {
	try {
		const res: any = await axios.delete(`/maintenance/work-orders/${data?.id}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// ─── Work Order CSV import (Session 67) ────────────────────────────
//
// Two-step: preview scores each Corrigo row's flat address against the
// plan client's LCPs (shared WO matching helpers, server-side) and
// buckets it matched / ambiguous / needs_review / duplicate / invalid;
// commit writes a WO per create-row via WorkOrderService.create, seeding
// the batch roster so the WO adopts the batch. See
// js_crm_backend work-order-import.service.ts + WO_CSV_IMPORT_PLAN.md.
//
// preview payload: { servicePlanId, rows: WoImportPreviewRow[] }
//   row: { rowIndex, externalId?, name?, line1?, city?, state?, zip?,
//          problemDescription?, priority?, nteAmount?, onSiteByAt?,
//          completeByAt?, acceptRejectByAt?, receivedAt? }
// returns: { rows: WoImportRowPreviewResult[] } — each carries status +
//   (for matched/ambiguous) candidate LCPs the operator can pick from.
export const previewWorkOrderImport = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders/import/preview`, {
			servicePlanId: data?.servicePlanId,
			rows: data?.rows || [],
		});
		// The controller returns a BARE ARRAY of per-row results
		// (WoImportRowPreviewResult[]). Normalize to { rows } so callers have
		// one shape to read. Tolerate a wrapped { rows } too, just in case.
		const payload: any = res?.data;
		if (Array.isArray(payload)) return { rows: payload };
		if (Array.isArray(payload?.rows)) return { rows: payload.rows };
		return { rows: [] };
	} catch (e: any) {
		return { rows: [], error: true };
	}
};

// commit payload: { servicePlanId, groupId, rows: WoImportCommitRow[] }
//   row: preview row + { action: 'create'|'skip', matchedLcpId?,
//                        matchedLocationId? }
// Non-transactional + re-runnable server-side (dedupes on externalId
// again at commit), so a partial failure is safe to retry. Returns
// { rows: WoImportCommitResult[] } with per-row outcome.
export const commitWorkOrderImport = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders/import/commit`, {
			servicePlanId: data?.servicePlanId,
			groupId: data?.groupId,
			rows: data?.rows || [],
		});
		return res?.data ? res.data : { rows: [] };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { rows: [], error: true };
	}
};

// WO case-file comment log ("Attachments & comms"). List returns the
// paginated NoteService shape ({ items, total, ... }); add posts a
// single note and returns the created note.
export const getWorkOrderNotes = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/work-orders/${data?.id}/notes`);
		return res?.data ? res.data : { items: [] };
	} catch (e: any) {
		return e?.response?.data ? e.response.data : { items: [] };
	}
};

export const addWorkOrderNote = async ({ data, note }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders/${data?.id}/notes`, { note });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// The work order's activity log — recorded events (who did what,
// when), newest first. Returns [] on older API builds that lack the
// endpoint, so the case file falls back to chain-derived history.
export const getWorkOrderEvents = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/work-orders/${data?.id}/events`, {
			excludeInterceptor: true,
		} as any);
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

// Attach / clear the measured map a work order was priced from. Pass
// mulchMapId=null to clear. Returns the updated WorkOrder (with the
// mulchMap relation loaded) or an error payload.
export const setWorkOrderMap = async ({ data, mulchMapId }: any) => {
	try {
		const res: any = await axios.patch(`/maintenance/work-orders/${data?.id}/map`, {
			mulchMapId: mulchMapId ?? null,
		});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// ─── Recurring Work (Cadence Scheduler, §4.8 #1) ───────────────────
//
// "Recurring Work" in the UI; routes stay /maintenance/cadence/* (code
// debt, matching the module's cadence* identifiers). run-now triggers
// one plan's generation manually; runs reads the audit history that
// backs the Plan Detail "Recurring Work history" panel. See
// docs/maintenance-module/design/CADENCE_SCHEDULER.md §8.1.

export const runPlanRecurringWork = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/cadence/plans/${data?.id}/run-now`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getPlanRecurringWorkRuns = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/cadence/plans/${data?.id}/runs`, {
			params: data?.limit ? { limit: data.limit } : undefined,
		});
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

// ─── Contacts ───────────────────────────────────────────────────────

export const getClientContacts = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/contacts`, { params: { clientId: data?.clientId } });
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const createContact = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/contacts`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// ─── Update Client Templates (§3.9 #19) ─────────────────────────────────

export const getUpdateClientTemplates = async ({ data }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/projects/${data?.id}/update-client-templates`);
		return res?.data ? res.data : null;
	} catch (e: any) {
		return e?.response?.data ? e.response.data : null;
	}
};

// ─── CSV Import (§3.9 #14) ──────────────────────────────────────────

export const previewMaintenanceImport = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/import/preview`, { rows: data?.rows });
		return res?.data ? res.data : [];
	} catch (e: any) {
		return e?.response?.data ? e.response.data : [];
	}
};

export const commitMaintenanceImport = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/maintenance/import/commit`, {
			servicePlanId: data?.servicePlanId,
			rows: data?.rows,
		});
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/* Attach existing work (2026-10-15): tie an existing RFQ (and the quote /
 * project under it) to a work order or a plan, by id. */
export const getWorkOrderRfqCandidates = async (id: string): Promise<any[] | { error: string }> => {
	try {
		const res: any = await axios.get(`/maintenance/work-orders/${id}/rfq-candidates`);
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return { error: e?.response?.data?.message ?? "Could not load RFQs for this site." };
	}
};

/* A 409 MOVE_FROM_PLAN comes back as { moveFromPlan: { message, moves } }:
 * work lives on ONE plan, so moving it off another needs confirmMove. */
const attachFail = (e: any, fallback: string) => {
	const d = e?.response?.data;
	if (d?.code === "MOVE_FROM_PLAN") return { moveFromPlan: { message: d.message, moves: d.moves ?? [] } };
	return { error: d?.message ?? fallback };
};

export const attachRfqToWorkOrder = async (id: string, rfqId: string, confirmMove = false): Promise<any> => {
	try {
		const res: any = await axios.post(`/maintenance/work-orders/${id}/attach-rfq`, { rfqId, ...(confirmMove ? { confirmMove: true } : {}) });
		return res?.data ?? {};
	} catch (e: any) {
		return attachFail(e, "Could not attach the RFQ.");
	}
};

export const getPlanAttachableWork = async (planId: string, search?: string): Promise<any[] | { error: string }> => {
	try {
		const res: any = await axios.get(`/maintenance/plans/${planId}/attachable-work`, { params: search ? { search } : {} });
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return { error: e?.response?.data?.message ?? "Could not load existing work." };
	}
};

export const attachWorkToPlan = async (planId: string, rfqIds: string[], confirmMove = false): Promise<any> => {
	try {
		const res: any = await axios.post(`/maintenance/plans/${planId}/attach-work`, { rfqIds, ...(confirmMove ? { confirmMove: true } : {}) });
		return res?.data ?? {};
	} catch (e: any) {
		return attachFail(e, "Could not add the work to the plan.");
	}
};
